import React, { useCallback } from 'react';

import { eventPreventDefault } from '@lyearn/core/utils/events';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { DotSeparator, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import ActionList from '@/components/LegacyMediaContainer/ActionList';
import {
  listViewResource,
  listViewResourceMediumSize,
} from '@/components/LegacyMediaContainer/style';
import { MediaDisplay, MediaSize, MediasProps } from '@/components/LegacyMediaContainer/types';
import { getFileCategory, getFileIconForMedia } from '@/features/media/utils';
import getFileNameAndExtension from '@/modules/classrooms/modules/files/utils/getFileNameAndExtension';
import { MediaKindEnum } from '@/types/schema';
import fileSize from '@/utils/fileSize';

function MediaItem({
  actions,
  className,
  media,
  size,
  onAction,
  onClick,
}: Pick<MediasProps, 'actions' | 'className' | 'media' | 'size' | 'onAction' | 'onClick'>) {
  const { mimeType, name, size: mediaSize } = media;
  const fileCategory = getFileCategory(mimeType!);
  const Icon = getFileIconForMedia(media);
  const { name: fileName, extension: fileExtension } = getFileNameAndExtension(name);

  const onClickHandler = useCallback(
    (event: React.MouseEvent) => {
      eventPreventDefault(event);
      onClick?.(media);
    },
    [media, onClick],
  );

  return (
    <Stack
      alignItems="center"
      className={cn(
        size === MediaSize.SMALL ? listViewResource : listViewResourceMediumSize,
        'media-item group relative mb-12 rounded border border-border bg-bg-primary py-12 ps-12 pe-18 hover:bg-bg-hover-light active:bg-bg-onclick',
        { 'h-64 ': size === MediaSize.SMALL || size === MediaSize.MEDIUM },
        className,
      )}
      data-size={size}
      justifyContent="between"
      role="button"
      onClick={onClickHandler}>
      <Stack>
        {media.kind === MediaKindEnum.Image && media.url ? (
          <Image
            alt={name}
            className="h-40 w-32 rounded object-cover me-12"
            height={40}
            src={media.url}
            width={32}
          />
        ) : null}
        <Icon
          className="flex h-40 w-32 flex-shrink-0 me-12"
          style={media.kind === MediaKindEnum.Image && media.url ? { display: 'none' } : undefined}
        />
        <Stack className="min-w-0" direction="col">
          <Typography className="truncate" color="text-text-primary" variant="body-short-02">
            {fileName}
          </Typography>
          <DotSeparator className="h-16" gapX={6}>
            {mediaSize ? (
              <Typography className="uppercase" color="text-text-secondary" variant="body-short-03">
                {fileSize(mediaSize) as string}
              </Typography>
            ) : null}
            <TypographyWithTooltip
              className="uppercase"
              color="text-text-secondary"
              numberOfLines={1}
              variant="body-short-03">
              {fileExtension || fileCategory}
            </TypographyWithTooltip>
          </DotSeparator>
        </Stack>
      </Stack>
      <ActionList
        actions={actions}
        className={size === MediaSize.MEDIUM ? '-mt-4 -me-2' : undefined}
        displayMode={MediaDisplay.LIST}
        media={media}
        onAction={onAction}
      />
    </Stack>
  );
}

export default MediaItem;
